import { Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'

interface InfoTagProps {
  label: string
  explanation: string
  /** Drop the text and keep the icon, for rows with no room for it. Stays the accessible name. */
  iconOnly?: boolean
  /** Shorter text shown below `md`; `label` stays the accessible name. */
  mobileLabel?: string
}

/** A small badge with an info-icon popover explaining it — reused for every tag that needs a definition. */
export function InfoTag({ label, explanation, iconOnly, mobileLabel }: InfoTagProps) {
  const track = useTrackQuotesEvent()

  return (
    <Popover onOpenChange={(open) => open && track(QuotesEvent.CARD_INFO_OPENED, { info_label: label })}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={iconOnly ? 'icon-sm' : 'sm'}
          aria-label={iconOnly || mobileLabel ? label : undefined}
          className={cn(
            'text-ink-500 hover:text-foreground hover:bg-transparent',
            // No pill: its padding was the last 16px between the label and the price line.
            !iconOnly && 'h-6 gap-1 px-0 text-xs font-normal has-[>svg]:px-0'
          )}
        >
          {!iconOnly &&
            (mobileLabel ? (
              <>
                <span className="md:hidden">{mobileLabel}</span>
                <span className="max-md:hidden">{label}</span>
              </>
            ) : (
              label
            ))}
          <Info />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm">{explanation}</PopoverContent>
    </Popover>
  )
}
